<template>
  <div class="work-toolbar">
    <div v-if="searchExpanded" class="work-toolbar__search">
      <icon-search class="work-toolbar__search-icon" />
      <input
        ref="searchInput"
        :value="searchQuery"
        type="text"
        class="work-toolbar__search-input"
        :placeholder="$t('assistantCatHome.workSelect.searchHint')"
        @input="onSearchInput"
      />
      <button type="button" class="work-toolbar__close" :aria-label="$t('common.close')" @click="onCloseSearch" />
    </div>
    <template v-else>
      <button
        type="button"
        class="work-toolbar__icon"
        :class="{ 'work-toolbar__icon--active': !!searchQuery }"
        :aria-label="$t('assistantCatHome.workSelect.searchHint')"
        @click="onOpenSearch"
      >
        <icon-search class="work-toolbar__svg" />
      </button>
      <button
        type="button"
        class="work-toolbar__icon"
        :class="{ 'work-toolbar__icon--active': hasActiveFilter }"
        :aria-label="$t('homePage.novelList.filter.dialogTitle')"
        @click="$emit('open-filter')"
      >
        <icon-slider class="work-toolbar__svg" />
      </button>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import IconSearch from "~icons/figma/Search";
import IconSlider from "~icons/figma/Slider";

export default Vue.extend({
  name: "AssistantCatHomeWorkToolbar",
  components: {
    IconSearch,
    IconSlider,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    searchExpanded: {
      type: Boolean,
      default: false,
    },
    hasActiveFilter: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    searchExpanded(expanded: boolean) {
      if (expanded) {
        this.$nextTick(() => {
          const input = this.$refs.searchInput as HTMLInputElement | undefined;
          input?.focus();
        });
      }
    },
  },
  methods: {
    onSearchInput(event: Event) {
      const target = event.target as HTMLInputElement;
      this.$emit("update:searchQuery", target.value);
    },
    onOpenSearch() {
      this.$emit("update:searchExpanded", true);
    },
    onCloseSearch() {
      this.$emit("update:searchQuery", "");
      this.$emit("update:searchExpanded", false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.work-toolbar {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

@include mq("sp") {
  .work-toolbar {
    padding: 0 16px 8px;
  }
}

.work-toolbar__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: var(--radius-rounded);
  background: var(--color-surface-base);
  color: var(--color-text-normal);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.work-toolbar__icon--active {
  color: var(--color-primary);
}

.work-toolbar__svg {
  font-size: var(--font-size-lg);
}

.work-toolbar__search {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  height: 40px;
  padding: 0 8px 0 12px;
  border-radius: 20px;
  background: var(--color-surface-base);
}

.work-toolbar__search-icon {
  flex-shrink: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.work-toolbar__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  margin-left: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);

  @include mq("sp") {
    font-size: var(--font-size-md);
  }

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--color-text-low);
  }
}

.work-toolbar__close {
  position: relative;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  cursor: pointer;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 1.5px;
    background-color: var(--color-text-low);
    border-radius: 1px;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}
</style>
